import { useParams, Link } from 'react-router-dom';
import { categories, categoryVideos, CategoryVideo } from '@/mocks/categories';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';

const siteUrl = 'https://www.91short.com';

function VideoCard({ video }: { video: CategoryVideo }) {
  return (
    <Link
      to={`/article/${video.id}`}
      className="group cursor-pointer"
    >
      <div className="relative rounded-2xl overflow-hidden mb-3 aspect-[3/4] bg-background-200">
        <img
          src={video.cover}
          alt={video.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          title={`91short - ${video.title}`}
        />
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
          <span className="text-background-50 text-xs font-medium bg-black/40 px-2.5 py-1 rounded-md">
            {video.views}播放
          </span>
        </div>
        <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm text-background-50 text-xs px-2 py-1 rounded-md">
          {video.duration}
        </div>
      </div>
      <h3 className="text-sm font-medium text-foreground-950 leading-snug line-clamp-2 mb-1.5 group-hover:text-primary-600 transition-colors">
        {video.title}
      </h3>
      <div className="flex items-center gap-2 text-xs text-foreground-500">
        <span>@{video.author}</span>
        <span className="text-foreground-300">·</span>
        <span><i className="ri-heart-line mr-0.5"></i>{video.likes}</span>
      </div>
    </Link>
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const currentCategory = categories.find((c) => c.slug === slug);
  const videos = slug ? categoryVideos[slug] || [] : [];

  if (!currentCategory) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-2">分类未找到</h1>
            <p className="text-foreground-500 mb-6">该分类不存在或已被移除</p>
            <Link to="/" className="px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const categorySchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        name: `${currentCategory.name} - 91short短视频`,
        description: currentCategory.description,
        url: `${siteUrl}/category/${currentCategory.slug}`,
        isPartOf: { '@type': 'WebSite', name: '91short', url: siteUrl },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: currentCategory.name },
        ],
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${currentCategory.name} - 91short短视频 | 浏览精选${currentCategory.name}内容`}
        description={currentCategory.description}
        keywords={`91short,${currentCategory.name},短视频,91short视频`}
        canonicalUrl={`${siteUrl}/category/${currentCategory.slug}`}
        schemaData={categorySchema}
      />

      <Navbar />

      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-950 font-medium whitespace-nowrap">{currentCategory.name}</span>
          </nav>

          <div className="mb-12">
            <div className="flex items-center gap-4 mb-4">
              <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${currentCategory.color} flex items-center justify-center`}>
                <i className={`${currentCategory.icon} text-2xl text-background-50`}></i>
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {currentCategory.name}
                </h1>
                <p className="text-sm text-foreground-500 mt-1">
                  共 {currentCategory.videoCount.toLocaleString()} 个视频
                </p>
              </div>
            </div>
            <p className="text-foreground-600 text-sm max-w-2xl">
              {currentCategory.description}
            </p>
          </div>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} mr-1.5`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {videos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {videos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-vidicon-line text-3xl text-foreground-400"></i>
              </div>
              <h2 className="text-xl font-heading font-semibold text-foreground-950 mb-2">暂无视频</h2>
              <p className="text-foreground-500 text-sm">该分类下暂时没有视频内容，请稍后再来</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </>
  );
}